import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Package, Check, X, ShoppingCart, ChevronLeft, ChevronRight, Play } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p || 0);
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url && url.startsWith("fi-barbershop") ? `${API_BASE}/api/files/${url}` : url;

/**
 * ProductDetailModal - Reusable product detail popup with media slider.
 * Props:
 *   product:    product object (or null to close)
 *   onClose:    function to close
 *   onBuyNow:   function(product) - "Beli Sekarang" callback
 *   onAddCart:  function(product) - "+ Keranjang" callback (optional)
 *   showAddCart: boolean - show "+ Keranjang" button (default true)
 *   buyLabel:   custom label for buy button (default "Beli Sekarang")
 */
export default function ProductDetailModal({ product, onClose, onBuyNow, onAddCart, showAddCart = true, buyLabel = "Beli Sekarang", buyDisabled = false }) {
  const [idx, setIdx] = useState(0);

  // Media list: video first (if exists), then images
  const media = useMemo(() => {
    if (!product) return [];
    const items = [];
    if (product.video_url) items.push({ type: "video", url: resolveUrl(product.video_url) });
    const imgs = Array.isArray(product.images) && product.images.length > 0 ? product.images : (product.image_url ? [product.image_url] : []);
    imgs.forEach((u) => items.push({ type: "image", url: resolveUrl(u) }));
    return items;
  }, [product]);

  // Reset slider when product changes
  useEffect(() => { setIdx(0); }, [product?.id]);

  const next = () => setIdx((i) => (i + 1) % media.length);
  const prev = () => setIdx((i) => (i - 1 + media.length) % media.length);

  if (!product) return null;
  const current = media[idx];
  const hasMedia = media.length > 0;

  return (
    <Dialog open={!!product} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-3xl rounded-none p-0 overflow-hidden max-h-[92vh]" data-testid="product-detail-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 max-h-[92vh] overflow-hidden">
          {/* Media slider */}
          <div className="bg-[#1A1A1A] relative aspect-square md:aspect-auto md:h-full flex items-center justify-center overflow-hidden" data-testid="product-media-slider">
            {hasMedia ? (
              <>
                {current.type === "video" ? (
                  <video
                    key={current.url}
                    src={current.url}
                    className="w-full h-full object-cover bg-black"
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    data-testid="product-video"
                  />
                ) : (
                  <img src={current.url} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
                )}
                {media.length > 1 && (
                  <>
                    <button type="button" onClick={prev} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-10" data-testid="slider-prev">
                      <ChevronLeft size={18} />
                    </button>
                    <button type="button" onClick={next} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-10" data-testid="slider-next">
                      <ChevronRight size={18} />
                    </button>
                    {/* Thumbnails */}
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 px-2 z-10">
                      {media.map((m, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setIdx(i)}
                          className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-[#E60000] w-6" : "bg-white/60 hover:bg-white"}`}
                          aria-label={`Go to media ${i + 1}`}
                          data-testid={`slider-dot-${i}`}
                        />
                      ))}
                    </div>
                    {/* Counter */}
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 z-10 flex items-center gap-1">
                      {current.type === "video" && <Play size={10} />}
                      {idx + 1}/{media.length}
                    </div>
                  </>
                )}
              </>
            ) : (
              <Package size={64} className="text-gray-700" />
            )}
          </div>

          {/* Info */}
          <div className="p-6 md:p-7 flex flex-col overflow-y-auto" style={{ maxHeight: "92vh" }}>
            <Badge className="text-[10px] bg-[#262626] text-gray-400 mb-2 self-start uppercase">{product.category}</Badge>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-2" data-testid="product-detail-name">{product.name}</h2>
            <p className="font-heading text-3xl font-bold text-[#E60000] mb-4" data-testid="product-detail-price">{formatPrice(product.price)}</p>
            <div className="flex items-center gap-2 mb-4">
              {product.stock > 0 ? (
                <span className="flex items-center gap-1 text-green-400 text-xs font-bold uppercase tracking-wider" data-testid="product-detail-stock">
                  <Check size={14} /> Stok Tersedia ({product.stock})
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-400 text-xs font-bold uppercase tracking-wider">
                  <X size={14} /> Stok Habis
                </span>
              )}
            </div>
            <div className="flex-1 mb-6">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Deskripsi</p>
              <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line" data-testid="product-detail-desc">
                {product.description || "Tidak ada deskripsi produk."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-[#262626] sticky bottom-0 bg-[#0A0A0A]">
              <Button
                onClick={() => onBuyNow?.(product)}
                disabled={buyDisabled || product.stock <= 0}
                className="flex-1 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-11 text-sm disabled:opacity-40"
                data-testid="buy-now-btn"
              >
                <ShoppingCart size={14} className="mr-2" /> {buyLabel}
              </Button>
              {showAddCart && (
                <Button
                  onClick={() => onAddCart?.(product)}
                  disabled={product.stock <= 0}
                  variant="outline"
                  className="flex-1 border-[#262626] hover:border-[#E60000] text-white hover:text-[#E60000] hover:bg-transparent font-bold uppercase tracking-wider rounded-none h-11 text-sm"
                  data-testid="add-to-cart-modal-btn"
                >
                  + Keranjang
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
